/**
 * Centralized tool name registry.
 *
 * All tools MUST import their name from here and tool
 * name MUST be globally unique.
 */
import { Literal, Static, Union } from "runtypes";

export const TOOL_NAMES = {
  // View and search tools
  VIEW_CELL_OUTPUTS: "ViewCellOutputs",
  GET_CELL_CODE: "GetCellCode",
  FIND_TABLE_SCHEMAS: "FindTableSchemas",
  FIND_TABLE_SCHEMAS_SEMANTIC_AUTHORING: "FindTableSchemasSemanticAuthoring",
  RUN_SQL: "RunSQL",
  RUN_SEMANTIC_SQL: "RunSemanticSql",
  GET_KERNEL_PACKAGES_INFO: "GetKernelPackagesInfo",
  SEARCH_HEX_DOCS: "SearchHexDocs",
  LOOK_UP_PROJECT: "LookUpProject",
  SEARCH_TOOLS: "SearchTools",
  // Ask/Threads-specific tool for looking at **another** project
  VIEW_PROJECT: "ViewProject",
  SEARCH_GUIDES: "SearchGuides",
  GET_FILE_CONTENTS: "GetFileContents",

  // Run cells tools
  RUN_APP: "RunApp",

  FIND_RELATED_PROJECTS: "FindRelatedProjects",
  FIND_SEMANTIC_COLUMNS: "FindSemanticColumns",
  LIST_SEMANTIC_INFO: "ListSemanticInfo",
  PROJECT_SEARCH: "ProjectSearch",

  // Create cell tools
  CREATE_MARKDOWN_CELL: "CreateMarkdownCell",
  CREATE_SECTION: "CreateSection",
  CREATE_SQL_CELL: "CreateSqlCell",
  CREATE_PYTHON_CELL: "CreatePythonCell",
  CREATE_PIVOT_CELL: "CreatePivotCell",
  CREATE_CHART_CELL: "CreateChartCell",
  CREATE_METRIC_CELL: "CreateMetricCell",
  CREATE_INPUT_CELL: "CreateInputCell",

  // Edit cell tools
  EDIT_REPLACE_MARKDOWN_CELL: "EditReplaceMarkdownCell",
  EDIT_REPLACE_SQL_CELL: "EditReplaceSqlCell",
  EDIT_REPLACE_PYTHON_CELL: "EditReplacePythonCell",
  EDIT_REPLACE_CHART_CELL: "EditReplaceChartCell",
  STYLE_CHART_CELL: "StyleChartCell",

  // Diff edit tools
  EDIT_MARKDOWN_CELL_DIFF: "EditMarkdownCellDiff",
  EDIT_SQL_CELL_DIFF: "EditSqlCellDiff",
  EDIT_PYTHON_CELL_DIFF: "EditPythonCellDiff",
  EDIT_PIVOT_CELL: "EditPivotCell",
  EDIT_METRIC_CELL: "EditMetricCell",
  EDIT_INPUT_CELL: "EditInputCell",

  // Vis cell conversion tools
  UPGRADE_LEGACY_CHART_CELL_TO_EXPLORE: "UpgradeLegacyChartCellToExplore",
  DUPLICATE_LEGACY_PIVOT_CELL: "DuplicateLegacyPivotCell",

  // Other cell tools
  MOVE_CELLS: "MoveCells",
  DELETE_CELLS: "DeleteCells",
  SET_CELLS_IN_APP: "SetCellsInApp",

  // Other tools
  PREVIEW_SEMANTIC_DATA: "PreviewSemanticData",
  CREATE_SEMANTIC_TABLE: "CreateSemanticTable",

  // Semantic authoring tools
  BOOTSTRAP_SEMANTIC_DATA_MODEL_FROM_TABLE:
    "BootstrapSemanticDataModelFromTable",
  CREATE_SEMANTIC_PROJECT_FILE: "CreateSemanticProjectFile",
  EDIT_SEMANTIC_PROJECT_FILE: "EditSemanticProjectFile",
  RENAME_SEMANTIC_PROJECT_FILE: "RenameSemanticProjectFile",
  VALIDATE_SEMANTIC_PROJECT: "ValidateSemanticProject",

  // Data discovery tools
  DELEGATE_TO_DATA_DISCOVERY_AGENT: "DelegateToDataDiscoveryAgent", // Tool call to delegate search to a data discovery subagent.

  // Memory tools
  MANAGE_USER_MEMORY: "ManageUserMemory",

  // Chat with app tools
  SEARCH_VALID_INPUT_VALUES: "SearchValidInputValues",
  SET_APP_CELL_FILTERS_AND_SORT: "SetAppCellFiltersAndSort",
  SET_APP_INPUTS: "SetAppInputs",
  VIEW_DEFINITION: "ViewDefinition",
} as const;

type ToolNameValue = (typeof TOOL_NAMES)[keyof typeof TOOL_NAMES];

const TOOL_NAME_LITERALS = Object.values(TOOL_NAMES).map((name) =>
  Literal(name),
) as [Literal<ToolNameValue>, ...Literal<ToolNameValue>[]];

export const ToolNameLiteral = Union(...TOOL_NAME_LITERALS);
export type ToolName = Static<typeof ToolNameLiteral>;
